import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Ban, UserMinus, UserRoundCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CheckboxField, Label, NativeSelect, Textarea } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { errorText, Spinner } from "@/components/common";
import { api } from "@/lib/api";
import { money, type Money } from "@/lib/format";

/**
 * İşe müdahale (K-115): başka çalışana ata, hizmet vereni işten çıkar, iptal et. Yetki: tasks.manage.
 * Hepsi gerekçe ister ve denetim kaydına yazılır.
 */
export interface TaskForActions {
  id: string;
  status: string;
  assignedMemberId: string | null;
  members: { id: string; role: string; name: string }[];
}

type OnBehalf = "customer" | "provider" | "poji";
const ON_BEHALF: Record<OnBehalf, { label: string; hint: string }> = {
  poji: { label: "Poji kararı", hint: "Güvenlik, hava durumu, sistem hatası gibi durumlar." },
  customer: {
    label: "Müşteri istedi",
    hint: "İptal kuralı uygulanır: ücretsiz süre geçtiyse iptal ücreti alınır.",
  },
  provider: {
    label: "Hizmet veren yapamıyor",
    hint: "Müşteriden ücret alınmaz; hizmet veren performansına yazılır.",
  },
};

function Reason({
  id,
  value,
  onChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label htmlFor={id}>Gerekçe</Label>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={500}
        placeholder="Denetim kaydına yazılır"
      />
    </div>
  );
}

function CancelPreview({ taskId, on }: { taskId: string; on: OnBehalf }) {
  const q = useQuery({
    queryKey: ["admin-task-cancel-preview", taskId, on],
    queryFn: () =>
      api<{ free: boolean; fee: Money | null; providerCompensation: Money | null }>(
        `/v1/admin/tasks/${taskId}/cancel-preview?onBehalfOf=${on}`,
      ),
  });
  if (q.isPending) return <Spinner />;
  if (q.error) return <p className="text-sm text-destructive">{errorText(q.error)}</p>;
  return (
    <p className="rounded-md bg-muted px-3 py-2 text-sm">
      {q.data.free
        ? "Müşteriden ücret alınmaz."
        : `Müşteriden ${money(q.data.fee)} iptal ücreti; hizmet verene ${money(q.data.providerCompensation)}.`}
    </p>
  );
}

export function TaskActions({ task }: { task: TaskForActions }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-task", task.id] });
  const [reason, setReason] = React.useState("");
  const [on, setOn] = React.useState<OnBehalf>("poji");
  const others = task.members.filter((m) => m.id !== task.assignedMemberId);
  const [memberId, setMemberId] = React.useState("");
  const [force, setForce] = React.useState(false);
  const reset = () => {
    setReason("");
    setOn("poji");
    setMemberId(others[0]?.id ?? "");
    setForce(false);
  };
  const ok = reason.trim().length >= 3;
  const open = ["draft", "requested", "accepted"].includes(task.status);
  if (!open) return null;

  return (
    <div className="flex flex-wrap gap-2">
      {task.status === "accepted" && others.length > 0 && (
        <ActionDialog
          onOpen={reset}
          trigger={
            <Button variant="outline" size="sm">
              <UserRoundCog /> Başka çalışana ata
            </Button>
          }
          title="Başka çalışana ata"
          description="Aynı hizmet verenin başka bir çalışanı. Fiyat değişmez; yeni kişi bildirim alır ve sohbete eklenir."
          confirmLabel="Ata"
          canSubmit={ok && Boolean(memberId)}
          success="İş yeni çalışana atandı."
          onSubmit={async (key) => {
            await api(`/v1/admin/tasks/${task.id}/assign-member`, {
              method: "POST",
              idempotencyKey: key,
              body: { memberId, reason: reason.trim(), ...(force ? { force: true } : {}) },
            });
            await refresh();
          }}
        >
          <div className="grid gap-3">
            <div className="grid gap-2">
              <Label htmlFor="assign-member">Çalışan</Label>
              <NativeSelect
                id="assign-member"
                value={memberId}
                onChange={(e) => setMemberId(e.target.value)}
              >
                {others.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </NativeSelect>
            </div>
            <CheckboxField
              label="Takvimi kontrol etme"
              hint="Çalışan o saatte müsait görünmüyor ama telefonla onay verdiyse."
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
            />
            <Reason id="assign-reason" value={reason} onChange={setReason} />
          </div>
        </ActionDialog>
      )}
      {(task.status === "requested" || task.status === "accepted") && (
        <ActionDialog
          onOpen={reset}
          trigger={
            <Button variant="outline" size="sm">
              <UserMinus /> Hizmet vereni çıkar
            </Button>
          }
          title="Hizmet vereni işten çıkar"
          description="İş müşteriye geri döner: aynı saat için başka bir hizmet vereni tek dokunuşla seçer. Para çekilmez, sohbet kapanır."
          confirmLabel="Çıkar"
          confirmVariant="destructive"
          canSubmit={ok}
          success="Hizmet veren işten çıkarıldı; müşteriye bildirildi."
          onSubmit={async (key) => {
            await api(`/v1/admin/tasks/${task.id}/release`, {
              method: "POST",
              idempotencyKey: key,
              body: { reason: reason.trim() },
            });
            await refresh();
          }}
        >
          <Reason id="release-reason" value={reason} onChange={setReason} />
        </ActionDialog>
      )}
      <ActionDialog
        onOpen={reset}
        trigger={
          <Button variant="outline" size="sm">
            <Ban /> İptal et
          </Button>
        }
        title="İşi iptal et"
        description="İki taraf da bildirim alır. Geri alınamaz."
        confirmLabel="İptal et"
        confirmVariant="destructive"
        canSubmit={ok}
        success="İş iptal edildi."
        onSubmit={async (key) => {
          await api(`/v1/admin/tasks/${task.id}/cancel`, {
            method: "POST",
            idempotencyKey: key,
            body: { onBehalfOf: on, reason: reason.trim() },
          });
          await refresh();
        }}
      >
        <div className="grid gap-3">
          <div className="grid gap-2">
            <Label htmlFor="cancel-on">Kimin adına</Label>
            <NativeSelect
              id="cancel-on"
              value={on}
              onChange={(e) => setOn(e.target.value as OnBehalf)}
            >
              {(Object.keys(ON_BEHALF) as OnBehalf[]).map((k) => (
                <option key={k} value={k}>
                  {ON_BEHALF[k].label}
                </option>
              ))}
            </NativeSelect>
            <p className="text-xs text-muted-foreground">{ON_BEHALF[on].hint}</p>
          </div>
          <CancelPreview taskId={task.id} on={on} />
          <Reason id="cancel-reason" value={reason} onChange={setReason} />
        </div>
      </ActionDialog>
    </div>
  );
}
